// Logout with a confirmation (shared by the top bar menu and the settings page). The dialog says
// what stays on the device: unsent recordings and changes are kept by default (they are sent when
// the same user signs in again); deleting them is an explicit, confirmed choice. A recording that
// still runs in another tab is never deleted from here, and the dialog says so.
import { useState } from 'react';
import { logout } from '../lib/api';
import { plural } from '../lib/format';
import { getStatus } from '../lib/outbox';
import { routeToHash, navigate } from '../lib/router';
import { actingUserId, ownLocalWork, type LocalWork } from '../lib/session';
import { confirmDialog } from './Dialog';
import { confirmLeave } from './NavGuard';
import { dismissAllToasts, toast } from './Toasts';

/** "45 min", "20 s"; empty when unknown. */
function lengthText(ms: number): string {
  if (!(ms > 0)) return '';
  return ms >= 60_000 ? `${Math.round(ms / 60_000)} min` : `${Math.max(1, Math.round(ms / 1000))} s`;
}

/** "1 posnetek (45 min) in 2 spremembi" */
function unsentText(work: LocalWork): string {
  const parts: string[] = [];
  if (work.recordings > 0) {
    const recordings = plural(work.recordings, ['posnetek', 'posnetka', 'posnetki', 'posnetkov']);
    const length = lengthText(work.recordedMs);
    parts.push(length ? `${recordings} (${work.recordings > 1 ? 'skupaj ' : ''}${length})` : recordings);
  }
  if (work.changes > 0) parts.push(plural(work.changes, ['sprememba', 'spremembi', 'spremembe', 'sprememb']));
  return parts.join(' in ');
}

const RECORDING_ELSEWHERE = 'V drugem zavihku še poteka snemanje. Ostane v tej napravi; končajte ga v tistem zavihku.';

function LogoutMessage({ work, refused, onDiscardChange }: { work: LocalWork; refused: number; onDiscardChange: (discard: boolean) => void }) {
  const [discard, setDiscard] = useState(false);
  const unsent = work.recordings + work.changes > 0;
  return (
    <>
      {unsent ? (
        <p>
          Na pošiljanje na strežnik v tej napravi še čaka: <strong>{unsentText(work)}</strong>. Ti podatki ostanejo v napravi in bodo poslani,
          ko se znova prijavite. Drugi uporabniki jih ne vidijo.
        </p>
      ) : null}
      {work.recordingNow > 0 && <p>{RECORDING_ELSEWHERE}</p>}
      <p>
        Kopije dokumentov bodo iz te naprave odstranjene.
        {work.drafts > 0 && ` Odstranjeni bodo tudi še ne dodani ročni vnosi v nadzorih (${work.drafts}).`}
        {refused > 0 && ` Odstranjen bo tudi seznam zavrnjenih sprememb (${refused}).`}
      </p>
      {unsent && (
        <label className="checkline">
          <input
            type="checkbox"
            checked={discard}
            onChange={(e) => {
              setDiscard(e.target.checked);
              onDiscardChange(e.target.checked);
            }}
          />
          <span>Izbriši tudi neposlane posnetke in spremembe s te naprave</span>
        </label>
      )}
    </>
  );
}

/** Asks first; resolves true when the user was logged out. */
export async function confirmAndLogout(): Promise<boolean> {
  // A running recording is stopped and saved first (it belongs to this user's unsent work).
  if (!(await confirmLeave(routeToHash({ name: 'login' })))) return false;
  const userId = actingUserId();
  const work = await ownLocalWork(userId);
  const refused = getStatus().failed.length;
  let discard = false;
  const ok = await confirmDialog({
    title: 'Odjava',
    message: <LogoutMessage work={work} refused={refused} onDiscardChange={(value) => (discard = value)} />,
    confirmLabel: 'Odjava',
  });
  if (!ok) return false;
  if (discard) {
    const sure = await confirmDialog({
      title: 'Izbrišem neposlane podatke?',
      message:
        `Iz te naprave bo nepreklicno izbrisano: ${unsentText(work)}. Ti podatki ne bodo nikoli poslani na strežnik. Tega ni mogoče razveljaviti.` +
        (work.recordingNow > 0 ? ' Snemanje, ki še poteka v drugem zavihku, ostane.' : ''),
      confirmLabel: 'Izbriši in odjavi',
      cancelLabel: 'Prekliči',
      danger: true,
    });
    if (!sure) return false;
  }
  const result = await logout({ discardUnsent: discard });
  dismissAllToasts();
  navigate({ name: 'login' }, { replace: true });
  if (result.discardFailed) {
    toast('Odjava je uspela, neposlanih posnetkov in sprememb pa ni bilo mogoče izbrisati. Ostali so v tej napravi.', { kind: 'error', durationMs: 0 });
  } else if (result.keptRecordings > 0) {
    toast('Odjava je uspela. Snemanje, ki še poteka v drugem zavihku, ni izbrisano: ostane v tej napravi.', { kind: 'warning', durationMs: 0 });
  } else if (!result.serverNotified) {
    toast('Odjavljeni ste v tej napravi. Strežnik bo obveščen, ko bo povezava spet na voljo.', { kind: 'info' });
  }
  return true;
}
